import {
  AnimatedButtonContainer,
  AnimatedButtonRow,
  Button,
} from "@babackapp/customer-ui/components/button";
import {
  Header,
  HeaderDescription,
  HeaderTitle,
  SectionHeader,
} from "@babackapp/customer-ui/components/header";
import { WidgetPage } from "@babackapp/customer-ui/components/widget-page";
import {
  Article,
  ArticleFooter,
  ArticleImage,
  ArticleList,
  ArticleSubtitle,
  ArticleTitle,
} from "@babackapp/customer-ui/components/article";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@babackapp/customer-ui/components/tooltip";
import { formatPrice } from "@babackapp/return-widget-components/utils/format-price";
import { getLineItemDisplay } from "@babackapp/return-widget-components/utils/line-item-display";
import { EnterIcon } from "@radix-ui/react-icons";
import { InfoIcon, ShoppingBagIcon } from "lucide-react";
import { Suspense } from "react";
import { useShopNowResolve } from "../hooks/use-shop-now-resolve";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import { useAuth } from "@babackapp/auth-client/hooks";

function ShopNowResolveInner() {
  const {
    items,
    currency,
    cartTotal,
    taxMultiplier,
    exchangeCreditLineItems,
    orderLineItems,
    handleAddMore,
    handleConfirm,
  } = useShopNowResolve();
  const t = useCustomText();
  const { isAuthenticated } = useAuth();
  const connectedUserTooltip = t("misc.connectedUserData");

  return (
    <WidgetPage>
      <Header>
        <HeaderTitle>{t("shopNowResolve.title")}</HeaderTitle>
        <HeaderDescription>{t("shopNowResolve.description")}</HeaderDescription>
      </Header>

      <div className="w-full flex flex-col gap-6">
        {exchangeCreditLineItems.length > 0 && (
          <div className="flex flex-col gap-4">
            <SectionHeader>{t("shopNowResolve.returnedItems")}</SectionHeader>
            <ArticleList>
              {exchangeCreditLineItems.map((li) => {
                const display = getLineItemDisplay(
                  orderLineItems,
                  li.lineItemId,
                );
                const price =
                  display.price != null
                    ? formatPrice(display.price * li.quantity, currency)
                    : null;
                const subtitle = [display.subtitle, `×${li.quantity}`, price]
                  .filter(Boolean)
                  .join(" — ");
                return (
                  <Article key={li.lineItemId}>
                    <ArticleImage
                      src={display.image ?? undefined}
                      layoutId={`img-exchange-${li.lineItemId}`}
                    />
                    <ArticleTitle>{display.title}</ArticleTitle>
                    <ArticleSubtitle>{subtitle}</ArticleSubtitle>
                    {isAuthenticated && display.sku && (
                      <ArticleFooter className="flex items-center gap-1.5">
                        <span>{display.sku}</span>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <InfoIcon className="size-3.5 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent>
                            {connectedUserTooltip}
                          </TooltipContent>
                        </Tooltip>
                      </ArticleFooter>
                    )}
                  </Article>
                );
              })}
            </ArticleList>
          </div>
        )}

        <div className="flex flex-col gap-4">
          <SectionHeader>{t("shopNowResolve.sectionTitle")}</SectionHeader>
          <ArticleList>
            {items.map((item, i) => (
              <Article key={`${item.variantId}-${i}`}>
                {item.image && (
                  <ArticleImage
                    src={item.image}
                    layoutId={`img-shop-now-${item.variantId}`}
                  />
                )}
                <ArticleTitle>{item.title}</ArticleTitle>
                <ArticleSubtitle>
                  ×{item.quantity} ·{" "}
                  {formatPrice(
                    item.price * item.quantity * taxMultiplier,
                    currency,
                  )}
                </ArticleSubtitle>
              </Article>
            ))}
          </ArticleList>
          <div className="flex justify-between text-sm font-medium px-1">
            <span>{t("shopNowResolve.cartTotal")}</span>
            <span>{formatPrice(cartTotal, currency)}</span>
          </div>
        </div>
      </div>

      <AnimatedButtonRow variants={{}}>
        <AnimatedButtonContainer layoutId="shop-now-add-more" variants={{}}>
          <Button variant="secondary" onClick={handleAddMore}>
            <ShoppingBagIcon />
            {t("shopNowResolve.addMore")}
          </Button>
        </AnimatedButtonContainer>
        <AnimatedButtonContainer
          layoutId="shop-now-resolve-confirm"
          variants={{}}
        >
          <Button disabled={items.length === 0} onClick={handleConfirm}>
            <EnterIcon />
            {t("shopNowResolve.confirm")}
          </Button>
        </AnimatedButtonContainer>
      </AnimatedButtonRow>
    </WidgetPage>
  );
}

export function ShopNowResolvePage() {
  return (
    <Suspense fallback={null}>
      <ShopNowResolveInner />
    </Suspense>
  );
}
